import type {
  Breakpoint,
  ElementPosition,
  ElementStyle,
  ResponsiveElementOverride,
  ScriptElement,
} from './schema.types';

/**
 * Helpers for responsive (per-breakpoint) element editing.
 *
 * Storage model: `element.responsiveOverrides[bp] = { position?, style? }`.
 * Reads merge base ← override. Writes return a FULL `responsiveOverrides`
 * object so the caller can hand it straight to `updateElement` — the backend
 * shallow-merges (`Object.assign`), so a partial override object would drop
 * the other breakpoints (see INTEGRATION.md §4.1).
 */

/** Effective style at a breakpoint (base merged with the breakpoint override). */
export function effectiveElementStyle(
  el: ScriptElement,
  bp: Breakpoint | null,
): ElementStyle {
  const base = el.style ?? {};
  if (!bp) return base;
  return { ...base, ...(el.responsiveOverrides?.[bp]?.style ?? {}) };
}

/** Effective position at a breakpoint (base merged with the breakpoint override). */
export function effectiveElementPosition(
  el: ScriptElement,
  bp: Breakpoint | null,
): ElementPosition {
  const base = el.position ?? {};
  if (!bp) return base;
  return { ...base, ...(el.responsiveOverrides?.[bp]?.position ?? {}) };
}

/**
 * Build the FULL `responsiveOverrides` map after merging `delta` into the given
 * breakpoint's override. Pass the result to `updateElement(..., { responsiveOverrides })`.
 */
export function buildResponsiveOverride(
  el: ScriptElement,
  bp: Breakpoint,
  delta: { style?: Partial<ElementStyle>; position?: Partial<ElementPosition> },
): NonNullable<ScriptElement['responsiveOverrides']> {
  const all = { ...(el.responsiveOverrides ?? {}) };
  const prev: ResponsiveElementOverride = { ...(all[bp] ?? {}) };
  if (delta.style) prev.style = { ...(prev.style ?? {}), ...delta.style };
  if (delta.position) prev.position = { ...(prev.position ?? {}), ...delta.position };
  all[bp] = prev;
  return all;
}

/**
 * Apply a style delta at the active breakpoint and return the `updateElement`
 * patch. When `bp` is null the delta targets the base `style`; otherwise it
 * targets the breakpoint override. (`updateElement` already full-merges the
 * base `style` for the wire; for overrides we pass the full map.)
 */
export function patchElementStyle(
  el: ScriptElement,
  bp: Breakpoint | null,
  delta: Partial<ElementStyle>,
): Partial<ScriptElement> {
  if (!bp) return { style: delta };
  return { responsiveOverrides: buildResponsiveOverride(el, bp, { style: delta }) };
}

/** Same as {@link patchElementStyle} for position. */
export function patchElementPosition(
  el: ScriptElement,
  bp: Breakpoint | null,
  delta: Partial<ElementPosition>,
): Partial<ScriptElement> {
  if (!bp) return { position: delta };
  return { responsiveOverrides: buildResponsiveOverride(el, bp, { position: delta }) };
}
